import util from '../util.js';
import {
    DEFAULT_DISPLAY_CURRENCY_CODE,
    DISPLAY_CURRENCIES,
    createDisplayCurrencyStorageValue,
    findDisplayCurrency,
    getDisplayCurrency,
    getDisplayCurrencyIndex,
    getDisplayCurrencyText,
} from '../constants/displayCurrencies.js';
import { isPositiveDecimal } from '../util/decimal.mjs';

const LEGACY_DISPLAY_CURRENCY_ORDER = ['USD', 'CNY', 'JPY', 'TWD'];

function parseJson(value) {
    if (typeof value !== 'string' || value.trim() === '') {
        return value;
    }

    try {
        return JSON.parse(value);
    } catch (error) {
        return value;
    }
}

function restoreCache(key, value) {
    if (value === null || typeof value === 'undefined' || value === '') {
        util.delCache(key);
        return;
    }
    util.setCache(key, value);
}

function findCachedDisplayCurrency() {
    const localCurrency = findDisplayCurrency(
        parseJson(util.getCache('local-currency'))
    );
    if (localCurrency) {
        return localCurrency;
    }

    const legacyExchange = findDisplayCurrency(util.getCache('exchange'));
    if (legacyExchange) {
        return legacyExchange;
    }

    const legacyIndex = parseInt(util.getCache('exchangeIndex'), 10);
    if (
        Number.isInteger(legacyIndex)
        && legacyIndex >= 0
        && legacyIndex < LEGACY_DISPLAY_CURRENCY_ORDER.length
    ) {
        return getDisplayCurrency(LEGACY_DISPLAY_CURRENCY_ORDER[legacyIndex]);
    }

    return null;
}

export function getCachedDisplayCurrency() {
    return findCachedDisplayCurrency()
        || getDisplayCurrency(DEFAULT_DISPLAY_CURRENCY_CODE);
}

export function getCachedDisplayCurrencyRate() {
    const rate = util.getCache('local-fee');
    return isPositiveDecimal(rate) ? String(rate) : null;
}

export function getCachedDisplayCurrencyState() {
    const currency = findCachedDisplayCurrency();
    const rate = getCachedDisplayCurrencyRate();
    return currency && rate ? { currency, rate } : null;
}

function throwDisplayCurrencyResponseError(response) {
    if (!response || typeof response !== 'object') {
        return;
    }

    const hasErrorCode = typeof response.code !== 'undefined'
        && response.code !== 200;
    const hasFailureFlag = response.success === false;
    if (!hasErrorCode && !hasFailureFlag) {
        return;
    }

    const message = typeof response.message === 'string'
        && response.message.trim() !== ''
        ? response.message.trim()
        : 'Unable to load display currency rate';
    const error = new Error(message);
    if (typeof response.code !== 'undefined') {
        error.code = response.code;
    }
    error.displayCurrencyResponseError = true;
    throw error;
}

export function extractDisplayCurrencyRate(response, currencyCode) {
    const currency = getDisplayCurrency(currencyCode);
    const payload = response
        && typeof response === 'object'
        ? response.code === 200
            ? response.data
            // request clients may resolve either the full response or data.
            : typeof response.code === 'undefined'
                ? response
                : null
        : null;
    const rates = payload && typeof payload === 'object' ? payload.tether : null;
    const rate = rates && typeof rates === 'object'
        ? rates[currency.code.toLowerCase()]
        : null;

    if (!isPositiveDecimal(rate)) {
        throw new Error('Invalid display currency rate');
    }

    return String(rate);
}

export async function requestDisplayCurrencyRate(currencyCode, apiClient = null) {
    const currency = getDisplayCurrency(currencyCode);
    const client = apiClient || (await import('../api/common.js')).default;
    const response = await client.getCurrencyFee(currency.code);
    throwDisplayCurrencyResponseError(response);
    return extractDisplayCurrencyRate(response, currency.code);
}

export function persistDisplayCurrency(currencyCode, rate) {
    if (!isPositiveDecimal(rate)) {
        throw new Error('Invalid display currency rate');
    }

    const currency = getDisplayCurrency(currencyCode);
    const storageValue = createDisplayCurrencyStorageValue(currency);
    const nextValues = {
        'local-currency': JSON.stringify(storageValue),
        'local-fee': String(rate),
        exchange: getDisplayCurrencyText(currency),
        exchangeIndex: String(getDisplayCurrencyIndex(currency)),
    };
    const previousValues = Object.keys(nextValues).reduce((result, key) => {
        result[key] = util.getCache(key, null);
        return result;
    }, {});

    try {
        Object.entries(nextValues).forEach(([key, value]) => {
            util.setCache(key, value);
        });
    } catch (error) {
        Object.entries(previousValues).forEach(([key, value]) => {
            restoreCache(key, value);
        });
        throw error;
    }

    return {
        currency,
        storageValue,
        rate: String(rate),
        exchangeText: nextValues.exchange,
        exchangeIndex: getDisplayCurrencyIndex(currency),
    };
}

export function normalizeCachedDisplayCurrency() {
    const state = getCachedDisplayCurrencyState();
    if (!state) {
        return null;
    }
    return persistDisplayCurrency(state.currency.code, state.rate);
}

export { DISPLAY_CURRENCIES };
